/**
 * MobileRoutesPage - Circuit-inspired route planning for mobile
 * 
 * A mobile-first route planning experience inspired by Circuit Route Planner.
 * Features: Stop management, optimization, navigation, swipe actions.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { 
  MapPin, Plus, Navigation, Clock, Route, Loader2, Check, X,
  ChevronRight, MoreVertical, Upload, Settings, Play, Pause,
  RotateCcw, FileText, Share2, Trash2, Edit2, ArrowUpDown,
  Map as MapIcon, List, ChevronDown, ChevronUp, AlertCircle,
  Locate, Flag, CheckCircle2, XCircle, SkipForward
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../lib/api';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Create numbered marker icons
const createMarkerIcon = (number, status = 'pending') => {
  const colors = {
    pending: { bg: '#0d9488', border: '#0f766e' },      // Teal
    completed: { bg: '#22c55e', border: '#16a34a' },    // Green
    successful: { bg: '#22c55e', border: '#16a34a' },   // Green
    unsuccessful: { bg: '#ef4444', border: '#dc2626' }, // Red
    skipped: { bg: '#64748b', border: '#475569' },      // Slate
    start: { bg: '#3b82f6', border: '#2563eb' },        // Blue
    end: { bg: '#f97316', border: '#ea580c' },          // Orange
  };
  
  const color = colors[status] || colors.pending;
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 32px; height: 32px; border-radius: 50%;
        background: ${color.bg}; border: 3px solid ${color.border};
        display: flex; align-items: center; justify-content: center;
        color: white; font-weight: bold; font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        transform: translate(-50%, -50%);
      ">${number}</div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Map auto-fit component
function FitBounds({ stops, startCoords, endCoords }) {
  const map = useMap();
  
  useEffect(() => {
    const bounds = [];
    
    if (startCoords?.lat && startCoords?.lng) {
      bounds.push([startCoords.lat, startCoords.lng]);
    }
    
    stops?.forEach(stop => {
      if (stop.lat && stop.lng) {
        bounds.push([stop.lat, stop.lng]);
      }
    });
    
    if (endCoords?.lat && endCoords?.lng) {
      bounds.push([endCoords.lat, endCoords.lng]);
    }
    
    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
    }
  }, [stops, startCoords, endCoords, map]);
  
  return null;
}

// Mobile Stop Card Component
function MobileStopCard({ stop, index, onNavigate, onStatusChange, onEdit, onDelete, isActive }) {
  const [expanded, setExpanded] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  
  const statusColors = {
    pending: 'border-l-teal-500',
    completed: 'border-l-green-500',
    successful: 'border-l-green-500',
    unsuccessful: 'border-l-red-500',
    skipped: 'border-l-slate-500',
  };
  
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    if (!touchStart) return;
    const diff = e.touches[0].clientX - touchStart;
    // Limit swipe distance
    setSwipeOffset(Math.max(-100, Math.min(100, diff)));
  };
  
  const handleTouchEnd = () => {
    if (swipeOffset > 60) {
      // Swipe right - Mark as successful
      onStatusChange(stop.id, 'successful');
    } else if (swipeOffset < -60) {
      // Swipe left - Mark as unsuccessful
      onStatusChange(stop.id, 'unsuccessful');
    }
    setSwipeOffset(0);
    setTouchStart(null);
  };
  
  return (
    <div 
      className="relative overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Swipe indicators */}
      <div className="absolute inset-y-0 left-0 w-20 bg-green-500 flex items-center justify-center"
           style={{ opacity: swipeOffset > 30 ? 1 : 0 }}>
        <CheckCircle2 className="h-6 w-6 text-white" />
      </div>
      <div className="absolute inset-y-0 right-0 w-20 bg-red-500 flex items-center justify-center"
           style={{ opacity: swipeOffset < -30 ? 1 : 0 }}>
        <XCircle className="h-6 w-6 text-white" />
      </div>
      
      {/* Main card */}
      <Card 
        className={`border-l-4 ${statusColors[stop.status || 'pending']} bg-white dark:bg-slate-800/80 
                   ${isActive ? 'ring-2 ring-teal-500' : ''} transition-transform`}
        style={{ transform: `translateX(${swipeOffset}px)` }}
        data-testid={`stop-card-${stop.id}`}
      >
        <CardContent className="p-3">
          <div className="flex items-start gap-3">
            {/* Number badge */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0
                          ${stop.status === 'pending' ? 'bg-teal-500' : 
                            stop.status === 'successful' || stop.status === 'completed' ? 'bg-green-500' :
                            stop.status === 'unsuccessful' ? 'bg-red-500' : 'bg-slate-500'}`}>
              {index + 1}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Name/Case */}
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  {stop.defendant_name && (
                    <p className="font-medium text-slate-900 dark:text-white truncate">
                      {stop.defendant_name}
                    </p>
                  )}
                  {stop.case_number && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Case: {stop.case_number}
                    </p>
                  )}
                </div>
                
                {/* Actions dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onNavigate(stop)}>
                      <Navigation className="h-4 w-4 mr-2" />
                      Navigate
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onStatusChange(stop.id, 'successful')}>
                      <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                      Served
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onStatusChange(stop.id, 'unsuccessful')}>
                      <XCircle className="h-4 w-4 mr-2 text-red-500" />
                      Failed
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onStatusChange(stop.id, 'skipped')}>
                      <SkipForward className="h-4 w-4 mr-2 text-slate-500" />
                      Skip
                    </DropdownMenuItem>
                    {stop.status !== 'pending' && (
                      <DropdownMenuItem onClick={() => onStatusChange(stop.id, 'pending')}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reset
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onEdit(stop)}>
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDelete(stop.id)} className="text-red-500">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              {/* Address */}
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 line-clamp-2">
                {stop.address}
              </p>
              
              {/* ETA and distance */}
              {(stop.eta || stop.segment_distance) && (
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                  {stop.eta && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      ETA: {stop.eta}
                    </span>
                  )}
                  {stop.segment_distance && (
                    <span>{stop.segment_distance} mi</span>
                  )}
                </div>
              )}
              
              {/* Notes preview */}
              {stop.notes && (
                <button 
                  onClick={() => setExpanded(!expanded)}
                  className="text-xs text-teal-500 mt-1 flex items-center gap-1"
                >
                  {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  {expanded ? 'Hide notes' : 'Show notes'}
                </button>
              )}
              {expanded && stop.notes && (
                <p className="text-xs text-slate-500 mt-1 p-2 bg-slate-100 dark:bg-slate-700/50 rounded">
                  {stop.notes}
                </p>
              )}
            </div>
          </div>
          
          {/* Quick action buttons for pending stops */}
          {stop.status === 'pending' && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              <Button 
                size="sm" 
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                onClick={() => onNavigate(stop)}
                data-testid={`navigate-stop-${stop.id}`}
              >
                <Navigation className="h-4 w-4 mr-1" />
                Navigate
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="border-green-500 text-green-500 hover:bg-green-500/10"
                onClick={() => onStatusChange(stop.id, 'successful')}
                data-testid={`mark-served-${stop.id}`}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-500/10"
                onClick={() => onStatusChange(stop.id, 'unsuccessful')}
                data-testid={`mark-failed-${stop.id}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Route List Item
function RouteListItem({ route, isSelected, onSelect }) {
  const pendingCount = route.stops?.filter(s => s.status === 'pending').length || 0;
  const totalCount = route.stops?.length || 0;
  const progress = totalCount > 0 ? ((totalCount - pendingCount) / totalCount) * 100 : 0;
  
  return (
    <button
      onClick={() => onSelect(route)}
      className={`w-full text-left p-4 border-b border-slate-200 dark:border-slate-700 
                 transition-colors ${isSelected ? 'bg-teal-500/10' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}
      data-testid={`route-item-${route.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-slate-900 dark:text-white truncate">{route.name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{formatDate(route.date)}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
      </div>
      
      {/* Progress bar */}
      <div className="mt-2">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{totalCount - pendingCount}/{totalCount} stops</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-teal-500 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </button>
  );
}

// Helper function to format dates
function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return dateStr;
  }
}

// Helper function to format duration
function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return '0m';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

// Main Mobile Routes Page Component
export default function MobileRoutesPage({ profile }) {
  // View state
  const [view, setView] = useState('list'); // 'list', 'detail', 'map'
  
  // Routes data
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Directions/map data
  const [routeDirections, setRouteDirections] = useState(null);
  const [loadingDirections, setLoadingDirections] = useState(false);
  
  // Dialogs
  const [showNewRouteDialog, setShowNewRouteDialog] = useState(false);
  const [showAddStopDialog, setShowAddStopDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showRouteSettings, setShowRouteSettings] = useState(false);
  const [showEditStopDialog, setShowEditStopDialog] = useState(false);
  const [editingStop, setEditingStop] = useState(null);
  
  // Insert position for mid-route stop addition
  const [insertAfterStopId, setInsertAfterStopId] = useState('');
  
  // New route form
  const [newRoute, setNewRoute] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0],
    use_current_location: true,
    start_address: '',
    return_to_start: false,
    time_per_stop_minutes: 5
  });
  
  // New stop form
  const [newStop, setNewStop] = useState({
    address: '',
    defendant_name: '',
    case_number: '',
    notes: '',
    priority: 'normal',
    service_time_minutes: 5
  });
  
  // Address autocomplete
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCoords, setSelectedCoords] = useState({ lat: null, lng: null });
  const suggestionTimeoutRef = useRef(null);
  
  // Edit stop address autocomplete
  const [editAddressSuggestions, setEditAddressSuggestions] = useState([]);
  const [showEditSuggestions, setShowEditSuggestions] = useState(false);
  const editSuggestionTimeoutRef = useRef(null);
  
  // Optimization state
  const [optimizing, setOptimizing] = useState(false);
  
  // Navigation preference
  const [navPreference, setNavPreference] = useState(() => {
    return localStorage.getItem('servio_nav_preference') || 'google';
  });
  
  // File import
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Load routes
  const fetchRoutes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getRoutes({ limit: 50 });
      setRoutes(response.data.routes || []);
    } catch (error) {
      toast.error('Failed to load routes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Fetch directions when route is selected
  const fetchDirections = useCallback(async (routeId) => {
    if (!routeId) return;
    setLoadingDirections(true);
    try {
      const response = await api.getRouteDirections(routeId);
      setRouteDirections(response.data);
    } catch (error) {
      console.error('Failed to fetch directions:', error);
      setRouteDirections(null);
    } finally {
      setLoadingDirections(false);
    }
  }, []);

  // Handle route selection
  const handleSelectRoute = (route) => {
    setSelectedRoute(route);
    setView('detail');
    if (route?.stops?.length > 0) {
      fetchDirections(route.id);
    }
  };

  // Create new route
  const handleCreateRoute = async () => {
    if (!newRoute.name.trim()) {
      toast.error('Please enter a route name');
      return;
    }
    
    try {
      const response = await api.createRoute({
        name: newRoute.name,
        date: newRoute.date,
        use_current_location: newRoute.use_current_location,
        start_address: newRoute.start_address || null,
        return_to_start: newRoute.return_to_start,
        time_per_stop_minutes: newRoute.time_per_stop_minutes,
        stops: []
      });
      
      setRoutes([response.data, ...routes]);
      setSelectedRoute(response.data);
      setShowNewRouteDialog(false);
      setView('detail');
      setNewRoute({
        name: '',
        date: new Date().toISOString().split('T')[0],
        use_current_location: true,
        start_address: '',
        return_to_start: false,
        time_per_stop_minutes: 5
      });
      toast.success('Route created!');
    } catch (error) {
      toast.error('Failed to create route');
    }
  };

  // Add stop
  const handleAddStop = async () => {
    if (!newStop.address.trim()) {
      toast.error('Please enter an address');
      return;
    }
    
    if (!selectedRoute) return;
    
    try {
      // Use the mid-route API which handles geocoding and insertion
      const stopData = {
        defendant_name: newStop.defendant_name || 'New Stop',
        address: newStop.address,
        case_number: newStop.case_number || null,
        notes: newStop.notes || null,
        priority: newStop.priority,
        service_time_minutes: newStop.service_time_minutes || 5,
        insert_after_id: insertAfterStopId || null, // For mid-route insertion
        lat: selectedCoords.lat,
        lng: selectedCoords.lng
      };
      
      const response = await api.addStopToRoute(selectedRoute.id, stopData);
      
      // Refresh the route to get updated stops with recalculated ETAs
      const updatedRoute = await api.getRoute(selectedRoute.id);
      setSelectedRoute(updatedRoute.data);
      setRoutes(routes.map(r => r.id === updatedRoute.data.id ? updatedRoute.data : r));
      
      // Refresh directions for updated map
      fetchDirections(selectedRoute.id);
      
      setShowAddStopDialog(false);
      setNewStop({ address: '', defendant_name: '', case_number: '', notes: '', priority: 'normal', service_time_minutes: 5 });
      setSelectedCoords({ lat: null, lng: null });
      setInsertAfterStopId('');
      
      const insertedAfterStop = selectedRoute.stops?.find(s => s.id === insertAfterStopId);
      if (insertedAfterStop) {
        toast.success(`Stop added after "${insertedAfterStop.defendant_name || 'Stop'}"!`);
      } else {
        toast.success('Stop added!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add stop');
    }
  };

  // Edit stop handlers
  const handleEditStop = (stop) => {
    setEditingStop({ ...stop });
    setShowEditStopDialog(true);
  };

  const handleSaveEditedStop = async () => {
    if (!editingStop || !selectedRoute) return;
    
    try {
      const updatedStops = selectedRoute.stops.map(s => 
        s.id === editingStop.id ? {
          ...s,
          defendant_name: editingStop.defendant_name,
          case_number: editingStop.case_number,
          notes: editingStop.notes,
          priority: editingStop.priority,
          service_time_minutes: editingStop.service_time_minutes,
          // Address update would need re-geocoding - for now keep same
          address: editingStop.address
        } : s
      );
      
      const response = await api.updateRoute(selectedRoute.id, { stops: updatedStops });
      setSelectedRoute(response.data);
      setRoutes(routes.map(r => r.id === response.data.id ? response.data : r));
      setShowEditStopDialog(false);
      setEditingStop(null);
      toast.success('Stop updated!');
    } catch (error) {
      toast.error('Failed to update stop');
    }
  };

  const handleEditAddressChange = (value) => {
    setEditingStop({ ...editingStop, address: value });
    
    if (editSuggestionTimeoutRef.current) {
      clearTimeout(editSuggestionTimeoutRef.current);
    }
    
    if (value.length < 3) {
      setEditAddressSuggestions([]);
      setShowEditSuggestions(false);
      return;
    }
    
    editSuggestionTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await api.getAddressSuggestions(value);
        setEditAddressSuggestions(response.data.suggestions || []);
        setShowEditSuggestions(true);
      } catch (error) {
        setEditAddressSuggestions([]);
      }
    }, 300);
  };

  const handleSelectEditSuggestion = async (suggestion) => {
    setEditingStop({ 
      ...editingStop, 
      address: suggestion.address,
      lat: suggestion.lat,
      lng: suggestion.lng
    });
    setShowEditSuggestions(false);
    setEditAddressSuggestions([]);
  };

  // Address autocomplete
  const handleAddressChange = (value) => {
    setNewStop({ ...newStop, address: value });
    setSelectedCoords({ lat: null, lng: null });
    
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }
    
    if (value.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    suggestionTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await api.getAddressSuggestions(value);
        setAddressSuggestions(response.data.suggestions || []);
        setShowSuggestions(true);
      } catch (error) {
        setAddressSuggestions([]);
      }
    }, 300);
  };

  const handleSelectSuggestion = (suggestion) => {
    setNewStop({ ...newStop, address: suggestion.address });
    setSelectedCoords({ lat: suggestion.lat, lng: suggestion.lng });
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  // Re-optimize remaining stops from current position (for mid-route changes)
  const handleReoptimizeRemaining = async () => {
    if (!selectedRoute) return;
    
    const pendingStops = selectedRoute.stops?.filter(s => s.status === 'pending') || [];
    if (pendingStops.length < 2) {
      toast.error('Need at least 2 pending stops to re-optimize');
      return;
    }
    
    setOptimizing(true);
    try {
      let currentLat = null, currentLng = null;
      
      // Get current GPS position
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { 
              timeout: 10000,
              enableHighAccuracy: true 
            });
          });
          currentLat = position.coords.latitude;
          currentLng = position.coords.longitude;
        } catch {
          toast.warning('Could not get current location');
        }
      }
      
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      const response = await api.optimizeRoute(selectedRoute.id, currentLat, currentLng, currentTime);
      
      setSelectedRoute(prev => ({
        ...prev,
        stops: response.data.stops,
        total_distance: response.data.total_distance,
        total_duration: response.data.total_duration
      }));
      
      setRoutes(routes.map(r => r.id === selectedRoute.id ? {
        ...r,
        stops: response.data.stops,
        total_distance: response.data.total_distance
      } : r));
      
      fetchDirections(selectedRoute.id);
      toast.success('Route re-optimized from your current location!');
    } catch (error) {
      toast.error('Failed to re-optimize route');
    } finally {
      setOptimizing(false);
    }
  };

  // Optimize route
  const handleOptimize = async () => {
    if (!selectedRoute || selectedRoute.stops?.length < 2) {
      toast.error('Need at least 2 stops to optimize');
      return;
    }
    
    setOptimizing(true);
    try {
      let currentLat = null, currentLng = null;
      
      if (selectedRoute.use_current_location && navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
          });
          currentLat = position.coords.latitude;
          currentLng = position.coords.longitude;
        } catch {
          toast.warning('Could not get current location');
        }
      }
      
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      const response = await api.optimizeRoute(selectedRoute.id, currentLat, currentLng, currentTime);
      
      setSelectedRoute(prev => ({
        ...prev,
        stops: response.data.stops,
        total_distance: response.data.total_distance,
        total_duration: response.data.total_duration
      }));
      
      setRoutes(routes.map(r => r.id === selectedRoute.id ? {
        ...r,
        stops: response.data.stops,
        total_distance: response.data.total_distance
      } : r));
      
      fetchDirections(selectedRoute.id);
      toast.success('Route optimized!');
    } catch (error) {
      toast.error('Failed to optimize route');
    } finally {
      setOptimizing(false);
    }
  };

  // Update stop status
  const handleStopStatusChange = async (stopId, status) => {
    if (!selectedRoute) return;
    
    try {
      const response = await api.completeStop(selectedRoute.id, stopId, status);
      const backendResult = response.data;
      
      const updatedStops = backendResult.stops || selectedRoute.stops.map(s => 
        s.id === stopId ? { ...s, status, completed_at: new Date().toISOString() } : s
      );
      
      const allDone = updatedStops.every(s => 
        ['completed', 'successful', 'unsuccessful', 'skipped'].includes(s.status)
      );
      
      const remainingDistanceCalc = backendResult.remaining_distance ?? selectedRoute.remaining_distance ?? 0;
      const remainingDurationCalc = backendResult.remaining_duration ?? selectedRoute.remaining_duration ?? 0;
      
      setSelectedRoute(prev => ({ 
        ...prev, 
        stops: updatedStops,
        status: allDone ? 'completed' : 'in_progress',
        remaining_distance: remainingDistanceCalc,
        remaining_duration: remainingDurationCalc,
        estimated_end_time: backendResult.estimated_end_time ?? prev.estimated_end_time
      }));
      setRoutes(routes.map(r => r.id === selectedRoute.id ? { 
        ...r, 
        stops: updatedStops,
        status: allDone ? 'completed' : 'in_progress',
        remaining_distance: remainingDistanceCalc,
        remaining_duration: remainingDurationCalc
      } : r));
      
      const messages = {
        'successful': 'Marked as Served!',
        'unsuccessful': 'Marked as Failed',
        'skipped': 'Stop skipped',
        'pending': 'Reset to pending'
      };
      toast.success(messages[status] || 'Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Navigate to stop
  const handleNavigateToStop = (stop) => {
    if (!stop.address) return;
    
    let url;
    const coords = stop.lat && stop.lng ? { lat: stop.lat, lng: stop.lng } : null;
    
    switch (navPreference) {
      case 'google':
        url = coords 
          ? `https://www.google.com/maps/dir/?api=1&destination=${coords.lat},${coords.lng}&travelmode=driving&dir_action=navigate`
          : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stop.address)}&travelmode=driving&dir_action=navigate`;
        break;
      case 'apple':
        url = coords
          ? `https://maps.apple.com/?daddr=${coords.lat},${coords.lng}&dirflg=d`
          : `https://maps.apple.com/?daddr=${encodeURIComponent(stop.address)}&dirflg=d`;
        break;
      case 'waze':
        url = coords
          ? `https://waze.com/ul?ll=${coords.lat},${coords.lng}&navigate=yes`
          : `https://waze.com/ul?q=${encodeURIComponent(stop.address)}&navigate=yes`;
        break;
      default:
        url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stop.address)}&travelmode=driving`;
    }
    
    window.open(url, '_blank');
  };

  // Delete stop
  const handleDeleteStop = async (stopId) => {
    if (!selectedRoute) return;
    
    const updatedStops = selectedRoute.stops.filter(s => s.id !== stopId);
    
    try {
      const response = await api.updateRoute(selectedRoute.id, { stops: updatedStops });
      setSelectedRoute(response.data);
      setRoutes(routes.map(r => r.id === response.data.id ? response.data : r));
      fetchDirections(selectedRoute.id);
      toast.success('Stop removed');
    } catch (error) {
      toast.error('Failed to remove stop');
    }
  };

  // File import handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileName = file.name.toLowerCase();
    if (!validTypes.some(ext => fileName.endsWith(ext))) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    setImportFile(file);
    handleImportFile(file);
  };

  const handleImportFile = async (file) => {
    setImporting(true);
    try {
      const response = await api.importStopsFromFile(file);
      setImportPreview(response.data);
      if (response.data.successful > 0) {
        toast.success(`Parsed ${response.data.successful} addresses`);
      }
    } catch (error) {
      toast.error('Failed to parse file');
      setImportPreview(null);
    }
    setImporting(false);
  };

  const handleConfirmImport = async () => {
    if (!importPreview?.stops?.length || !selectedRoute) return;
    
    try {
      const updatedStops = [
        ...(selectedRoute.stops || []),
        ...importPreview.stops.map(stop => ({
          ...stop,
          status: 'pending'
        }))
      ];
      
      await api.updateRoute(selectedRoute.id, { stops: updatedStops });
      const updatedRoute = await api.getRoute(selectedRoute.id);
      
      setSelectedRoute(updatedRoute.data);
      setRoutes(routes.map(r => r.id === selectedRoute.id ? updatedRoute.data : r));
      
      toast.success(`Added ${importPreview.stops.length} stops!`);
      setShowImportDialog(false);
      setImportFile(null);
      setImportPreview(null);
    } catch (error) {
      toast.error('Failed to add stops');
    }
  };

  // Get next pending stop
  const getNextPendingStop = () => {
    return selectedRoute?.stops?.find(s => s.status === 'pending');
  };

  // Calculate stats
  const pendingStops = selectedRoute?.stops?.filter(s => s.status === 'pending').length || 0;
  const completedStops = (selectedRoute?.stops?.length || 0) - pendingStops;

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900" data-testid="mobile-routes-page">
      {/* List View - All Routes */}
      {view === 'list' && (
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  <Route className="h-5 w-5 text-teal-500" />
                  Routes
                </h1>
                <p className="text-sm text-slate-500">{routes.length} routes</p>
              </div>
              <Button 
                onClick={() => setShowNewRouteDialog(true)}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="new-route-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                New
              </Button>
            </div>
          </div>
          
          {/* Routes List */}
          <div className="flex-1 overflow-y-auto">
            {routes.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center p-4">
                <Route className="h-12 w-12 text-slate-400 mb-3" />
                <h3 className="font-medium text-slate-700 dark:text-slate-300">No routes yet</h3>
                <p className="text-sm text-slate-500 mt-1">Create your first route to get started</p>
                <Button 
                  onClick={() => setShowNewRouteDialog(true)}
                  className="mt-4 bg-teal-600 hover:bg-teal-700"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create Route
                </Button>
              </div>
            ) : (
              routes.map(route => (
                <RouteListItem
                  key={route.id}
                  route={route}
                  isSelected={selectedRoute?.id === route.id}
                  onSelect={handleSelectRoute}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Detail View - Selected Route */}
      {view === 'detail' && selectedRoute && (
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => { setView('list'); setSelectedRoute(null); }}
                className="p-2 -ml-2 text-slate-500 hover:text-slate-700"
              >
                <ChevronRight className="h-5 w-5 rotate-180" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="font-bold text-slate-900 dark:text-white truncate">{selectedRoute.name}</h1>
                <p className="text-xs text-slate-500">{formatDate(selectedRoute.date)}</p>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setView('map')}
                data-testid="show-map-btn"
              >
                <MapIcon className="h-4 w-4" />
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setShowRouteSettings(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowImportDialog(true)}>
                    <Upload className="h-4 w-4 mr-2" />
                    Import CSV
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <FileText className="h-4 w-4 mr-2" />
                    Export PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Share2 className="h-4 w-4 mr-2" />
                    Share Link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Stats bar */}
            <div className="flex items-center justify-between mt-3 p-2 bg-slate-100 dark:bg-slate-700/50 rounded-lg text-sm">
              <div className="text-center">
                <p className="font-semibold text-slate-900 dark:text-white">{selectedRoute.stops?.length || 0}</p>
                <p className="text-xs text-slate-500">Stops</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-slate-900 dark:text-white">{pendingStops}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-slate-900 dark:text-white">{selectedRoute.total_distance || 0} mi</p>
                <p className="text-xs text-slate-500">Distance</p>
              </div>
              <div className="text-center">
                <p className="font-semibold text-slate-900 dark:text-white">{formatDuration(selectedRoute.total_duration)}</p>
                <p className="text-xs text-slate-500">Time</p>
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-2 mt-3">
              <Button 
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                onClick={handleOptimize}
                disabled={optimizing || (selectedRoute.stops?.length || 0) < 2}
                data-testid="optimize-route-btn"
              >
                {optimizing ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <ArrowUpDown className="h-4 w-4 mr-1" />
                )}
                Optimize
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowAddStopDialog(true)}
                data-testid="add-stop-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Stop
              </Button>
            </div>
            
            {/* Re-optimize from current position - for mid-route changes */}
            {completedStops > 0 && pendingStops > 1 && (
              <Button 
                variant="outline"
                className="w-full mt-2 border-teal-500 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-950"
                onClick={handleReoptimizeRemaining}
                disabled={optimizing}
                data-testid="reoptimize-remaining-btn"
              >
                <Locate className="h-4 w-4 mr-2" />
                Re-optimize Remaining {pendingStops} Stops from Here
              </Button>
            )}
          </div>
          
          {/* Stops List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {(selectedRoute.stops?.length || 0) === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-center">
                <MapPin className="h-10 w-10 text-slate-400 mb-2" />
                <p className="text-slate-500">No stops yet</p>
                <p className="text-sm text-slate-400 mt-1">Add stops or import from CSV</p>
                <div className="flex gap-2 mt-3">
                  <Button 
                    size="sm"
                    onClick={() => setShowAddStopDialog(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Stop
                  </Button>
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={() => setShowImportDialog(true)}
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    Import
                  </Button>
                </div>
              </div>
            ) : (
              selectedRoute.stops.map((stop, index) => (
                <MobileStopCard
                  key={stop.id}
                  stop={stop}
                  index={index}
                  isActive={getNextPendingStop()?.id === stop.id}
                  onNavigate={handleNavigateToStop}
                  onStatusChange={handleStopStatusChange}
                  onEdit={handleEditStop}
                  onDelete={handleDeleteStop}
                />
              ))
            )}
          </div>
          
          {/* Bottom action bar for navigation */}
          {getNextPendingStop() && (
            <div className="p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 safe-area-bottom">
              <Button 
                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-lg"
                onClick={() => handleNavigateToStop(getNextPendingStop())}
                data-testid="navigate-next-btn"
              >
                <Navigation className="h-5 w-5 mr-2" />
                Navigate to Stop {selectedRoute.stops.findIndex(s => s.id === getNextPendingStop()?.id) + 1}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Map View */}
      {view === 'map' && selectedRoute && (
        <div className="flex flex-col h-full">
          {/* Minimal header */}
          <div className="absolute top-0 left-0 right-0 z-[1000] p-2">
            <div className="flex items-center justify-between bg-white/90 dark:bg-slate-800/90 backdrop-blur rounded-lg p-2 shadow-lg">
              <button 
                onClick={() => setView('detail')}
                className="p-2 text-slate-600 dark:text-slate-300"
              >
                <List className="h-5 w-5" />
              </button>
              <span className="font-medium text-slate-900 dark:text-white">{selectedRoute.name}</span>
              <span className="text-sm text-slate-500">{pendingStops} left</span>
            </div>
          </div>
          
          {/* Map */}
          <div className="flex-1 relative">
            <MapContainer
              center={[39.8283, -98.5795]}
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              zoomControl={false}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
              />
              
              <FitBounds 
                stops={selectedRoute.stops}
                startCoords={selectedRoute.start_lat ? { lat: selectedRoute.start_lat, lng: selectedRoute.start_lng } : null}
                endCoords={selectedRoute.end_lat ? { lat: selectedRoute.end_lat, lng: selectedRoute.end_lng } : null}
              />
              
              {/* Route polyline */}
              {routeDirections?.polyline && (
                <Polyline
                  positions={routeDirections.polyline}
                  color="#0d9488"
                  weight={4}
                  opacity={0.8}
                />
              )}
              
              {/* Stop markers */}
              {selectedRoute.stops?.map((stop, index) => (
                stop.lat && stop.lng && (
                  <Marker
                    key={stop.id}
                    position={[stop.lat, stop.lng]}
                    icon={createMarkerIcon(index + 1, stop.status)}
                  />
                )
              ))}
            </MapContainer>
          </div>
          
          {/* Bottom card with next stop */}
          {getNextPendingStop() && (
            <div className="absolute bottom-0 left-0 right-0 z-[1000] p-4 safe-area-bottom">
              <Card className="bg-white/95 dark:bg-slate-800/95 backdrop-blur shadow-lg">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold">
                      {selectedRoute.stops.findIndex(s => s.id === getNextPendingStop()?.id) + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">
                        {getNextPendingStop()?.defendant_name || 'Next Stop'}
                      </p>
                      <p className="text-sm text-slate-500 truncate">{getNextPendingStop()?.address}</p>
                    </div>
                    <Button 
                      className="bg-teal-600 hover:bg-teal-700"
                      onClick={() => handleNavigateToStop(getNextPendingStop())}
                    >
                      <Navigation className="h-4 w-4 mr-1" />
                      Go
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* New Route Dialog */}
      <Dialog open={showNewRouteDialog} onOpenChange={setShowNewRouteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Route</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Route Name</Label>
              <Input
                placeholder="e.g., Downtown Dallas Serves"
                value={newRoute.name}
                onChange={(e) => setNewRoute({ ...newRoute, name: e.target.value })}
                data-testid="route-name-input"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={newRoute.date}
                onChange={(e) => setNewRoute({ ...newRoute, date: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Start from current location</Label>
              <Switch
                checked={newRoute.use_current_location}
                onCheckedChange={(checked) => setNewRoute({ ...newRoute, use_current_location: checked })}
              />
            </div>
            {!newRoute.use_current_location && (
              <div>
                <Label>Start Address</Label>
                <Input
                  placeholder="Enter start address"
                  value={newRoute.start_address}
                  onChange={(e) => setNewRoute({ ...newRoute, start_address: e.target.value })}
                />
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label>Return to start</Label>
              <Switch
                checked={newRoute.return_to_start}
                onCheckedChange={(checked) => setNewRoute({ ...newRoute, return_to_start: checked })}
              />
            </div>
            <div>
              <Label>Time per stop (minutes)</Label>
              <Input
                type="number"
                min={1}
                max={60}
                value={newRoute.time_per_stop_minutes}
                onChange={(e) => setNewRoute({ ...newRoute, time_per_stop_minutes: parseInt(e.target.value) || 5 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRouteDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRoute} className="bg-teal-600 hover:bg-teal-700" data-testid="create-route-submit">
              Create Route
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stop Dialog */}
      <Dialog open={showAddStopDialog} onOpenChange={setShowAddStopDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Stop</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="relative">
              <Label>Address</Label>
              <Input
                placeholder="Enter address"
                value={newStop.address}
                onChange={(e) => handleAddressChange(e.target.value)}
                data-testid="stop-address-input"
              />
              {showSuggestions && addressSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {addressSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm"
                      onClick={() => handleSelectSuggestion(suggestion)}
                    >
                      {suggestion.address}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Defendant Name (optional)</Label>
              <Input
                placeholder="John Doe"
                value={newStop.defendant_name}
                onChange={(e) => setNewStop({ ...newStop, defendant_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Case Number (optional)</Label>
              <Input
                placeholder="2024-CV-12345"
                value={newStop.case_number}
                onChange={(e) => setNewStop({ ...newStop, case_number: e.target.value })}
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Gate code, apartment number, etc."
                value={newStop.notes}
                onChange={(e) => setNewStop({ ...newStop, notes: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <Label>Service Time (minutes)</Label>
              <Input
                type="number"
                min={1}
                max={120}
                value={newStop.service_time_minutes}
                onChange={(e) => setNewStop({ ...newStop, service_time_minutes: parseInt(e.target.value) || 5 })}
              />
              <p className="text-xs text-slate-500 mt-1">Estimated time at this stop</p>
            </div>
            
            {/* Insert Position - for mid-route addition */}
            {(selectedRoute?.stops?.length || 0) > 0 && (
              <div>
                <Label>Insert After</Label>
                <select
                  className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-sm"
                  value={insertAfterStopId}
                  onChange={(e) => setInsertAfterStopId(e.target.value)}
                >
                  <option value="">At the end of route</option>
                  <option value="start">At the beginning</option>
                  {selectedRoute.stops.map((stop, idx) => (
                    <option key={stop.id} value={stop.id}>
                      After Stop {idx + 1}: {stop.defendant_name || stop.address?.slice(0, 30)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {insertAfterStopId 
                    ? "Route will be re-optimized after insertion" 
                    : "New stop will be added at the end"}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStopDialog(false)}>Cancel</Button>
            <Button onClick={handleAddStop} className="bg-teal-600 hover:bg-teal-700" data-testid="add-stop-submit">
              Add Stop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Stop Dialog */}
      <Dialog open={showEditStopDialog} onOpenChange={setShowEditStopDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Stop</DialogTitle>
          </DialogHeader>
          {editingStop && (
            <div className="space-y-4 py-4">
              <div className="relative">
                <Label>Address</Label>
                <Input
                  placeholder="Enter address"
                  value={editingStop.address || ''}
                  onChange={(e) => handleEditAddressChange(e.target.value)}
                  data-testid="edit-stop-address-input"
                />
                {showEditSuggestions && editAddressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {editAddressSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm"
                        onClick={() => handleSelectEditSuggestion(suggestion)}
                      >
                        {suggestion.address}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label>Defendant Name</Label>
                <Input
                  placeholder="John Doe"
                  value={editingStop.defendant_name || ''}
                  onChange={(e) => setEditingStop({ ...editingStop, defendant_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Case Number</Label>
                <Input
                  placeholder="2024-CV-12345"
                  value={editingStop.case_number || ''}
                  onChange={(e) => setEditingStop({ ...editingStop, case_number: e.target.value })}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  placeholder="Gate code, apartment number, etc."
                  value={editingStop.notes || ''}
                  onChange={(e) => setEditingStop({ ...editingStop, notes: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <Label>Service Time (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  max={120}
                  value={editingStop.service_time_minutes || 5}
                  onChange={(e) => setEditingStop({ ...editingStop, service_time_minutes: parseInt(e.target.value) || 5 })}
                />
              </div>
              <div>
                <Label>Priority</Label>
                <select
                  className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-sm"
                  value={editingStop.priority || 'normal'}
                  onChange={(e) => setEditingStop({ ...editingStop, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High - Rush Service</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditStopDialog(false); setEditingStop(null); }}>
              Cancel
            </Button>
            <Button onClick={handleSaveEditedStop} className="bg-teal-600 hover:bg-teal-700" data-testid="save-edit-stop">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Route Settings Dialog */}
      <Dialog open={showRouteSettings} onOpenChange={setShowRouteSettings}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Route Settings</DialogTitle>
          </DialogHeader>
          {selectedRoute && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Route Name</Label>
                <Input
                  value={selectedRoute.name || ''}
                  onChange={(e) => setSelectedRoute({ ...selectedRoute, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={selectedRoute.date || ''}
                  onChange={(e) => setSelectedRoute({ ...selectedRoute, date: e.target.value })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Return to start</Label>
                <Switch
                  checked={selectedRoute.return_to_start || false}
                  onCheckedChange={(checked) => setSelectedRoute({ ...selectedRoute, return_to_start: checked })}
                />
              </div>
              <div>
                <Label>Default time per stop (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={selectedRoute.time_per_stop_minutes || 5}
                  onChange={(e) => setSelectedRoute({ ...selectedRoute, time_per_stop_minutes: parseInt(e.target.value) || 5 })}
                />
              </div>
              <div>
                <Label>Navigation App</Label>
                <select
                  className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-sm"
                  value={navPreference}
                  onChange={(e) => {
                    setNavPreference(e.target.value);
                    localStorage.setItem('servio_nav_preference', e.target.value);
                  }}
                >
                  <option value="google">Google Maps</option>
                  <option value="apple">Apple Maps</option>
                  <option value="waze">Waze</option>
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRouteSettings(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                try {
                  await api.updateRoute(selectedRoute.id, {
                    name: selectedRoute.name,
                    date: selectedRoute.date,
                    return_to_start: selectedRoute.return_to_start,
                    time_per_stop_minutes: selectedRoute.time_per_stop_minutes
                  });
                  setRoutes(routes.map(r => r.id === selectedRoute.id ? selectedRoute : r));
                  setShowRouteSettings(false);
                  toast.success('Settings saved!');
                } catch {
                  toast.error('Failed to save settings');
                }
              }}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Stops</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div 
              className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-teal-500 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={handleFileSelect}
              />
              <Upload className="h-10 w-10 text-slate-400 mx-auto mb-2" />
              <p className="text-slate-600 dark:text-slate-300">
                {importFile ? importFile.name : 'Click to upload CSV or Excel file'}
              </p>
              <p className="text-xs text-slate-400 mt-1">CSV, XLSX, XLS supported</p>
            </div>
            
            {importing && (
              <div className="flex items-center justify-center gap-2 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Processing file...</span>
              </div>
            )}
            
            {importPreview && (
              <div className="p-3 bg-slate-100 dark:bg-slate-700/50 rounded-lg">
                <p className="text-sm">
                  <span className="text-green-500 font-medium">{importPreview.successful}</span> addresses parsed
                  {importPreview.failed > 0 && (
                    <span className="text-red-500 ml-2">({importPreview.failed} failed)</span>
                  )}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportDialog(false); setImportFile(null); setImportPreview(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmImport} 
              disabled={!importPreview?.stops?.length}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Import {importPreview?.stops?.length || 0} Stops
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
